import omit from 'lodash/omit'

/**
 * Параметры которые надо исключать, при формировании ссылок
 * @type {Array}
 */
const EXCLUDED_PARAMS = ['viewtype', 'sort', 'per-page']
/**
 * Переназначить URL окна при помощи historyAPI.
 * @param {string} title Название состояния истории
 * @param {string} url Новый URL. Если append, то добавление к текущему урлу
 * @param {boolean} append Флаг на дополнение URL
 */
export const setWindowUrl = (
  {
    title = window.document.title,
    url,
    append,
  }) => {
  if (append) {
    url = window.location.origin + window.location.pathname + url
  }
  window.history.replaceState(null, title, url)
}

/**
 * Получает все параметры из URL.
 *
 * @param {string} [url], если не указан, то берётся url текущей страницы
 * @returns {Object} Объект с гет параметрами запроса.
 */
export const getUrlParameters = (url = location.href) => {
  let pageURL = url.split('?'),
    pageURLRemovedHash = pageURL.length > 1 ? pageURL[1].split('#')[0] : '',
    getParams = pageURLRemovedHash.split('&')
  return getParams.reduce(function (acc, cur) {
    let arr = cur.split('='),
      key = arr[0] ? arr[0] : '',
      value = arr[1] ? arr[1] : ''

    try {
      value = decodeURIComponent(value)
    } catch (e) {
      value = ''
    }
    if (key && value) {
      acc[key] = value
    }
    return acc
  }, {})
}

/**
 * Возвращает текущее значение GET параметра.
 *
 * @param {string} sParam Параметр, значение которого нужно узнать.
 * @param {string} [url] Опционально, URL, из которого будут парсится параметр, если не передан,
 * то параметры будут выделены из текущего URL страницы
 *
 * @returns {string} Значение параметра
 */
export const getUrlParameter = (sParam, url) => {
  const params = getUrlParameters(url)
  if (params[sParam] !== undefined) {
    // энкодим для доступности ввода амперсанда (&) и других символов в поисковой запрос
    if (sParam === 'q') {
      return encodeURIComponent(params[sParam])
    }
    return params[sParam]
  }
}

/**
 * Возвращает отфильтрованные GET параметры.
 * @param {Array} exclude Исключенные параметры. Также доступно частичное совпадение.
 * @param {string} [url=location.href] URL для фильтрации.
 * @returns {Object} Отфильтрованные параметры
 */
export const getFilteredUrlParameters = (exclude = [], url) => {
  const params = getUrlParameters(url)
  if (exclude.length) {
    let filter = []
    exclude.forEach(exclusion => {
      Object.keys(params).forEach(param => {
        if (param.includes(exclusion)) {
          filter.push(param)
        }
      })
    })
    return omit(params, filter)
  } else {
    return params
  }
}

/**
 * Формирует нужные GET параметры из урл в строку для новой ссылки.
 *
 * @param {Object} params Массив параметров, которые нужно добавить к ссылке. Например, ['currency', 'page']
 *
 * @returns {string} Строка вида 'currency=USD&page=2'
 */
export const getExistParamString = params => {
  let result = [], i
  for (i in params) {
    if (params.hasOwnProperty(i) && getUrlParameter(params[i])) {
      result.push(params[i] + '=' + getUrlParameter(params[i]))
    }
  }
  return result.join('&')
}

/**
 * Формирует GET параметры.
 *
 * @param {Object} params Массив параметров, которые нужно добавить к ссылке. Например, {'currency': 1, 'page': 2}
 *
 * @returns {string} Строка вида 'currency=1&page=2'
 */
export const getParamString = params => {
  let result = [], i
  for (i in params) {
    if (params.hasOwnProperty(i)) {
      result.push(i + '=' + params[i])
    }
  }
  return result.join('&')
}

/**
 * Добавляет параметры к ссылке
 *
 * @param {string} url Адрес ссылки
 * @param {Array} existParams Массив параметров из адресной строки, которые нужно добавить к ссылке.
 * @param {Object} params Объект с параметрами, которые нужно добавить к ссылке.
 * @param {boolean} exclude Исключать параметры из url описанные в EXCLUDED_PARAMS.
 * @returns {string} Ссылка с новыми параметрами.
 */
export const to = (url, existParams, params = {}, exclude = true) => {
  if (exclude) {
    for (const i in params) {
      if (params.hasOwnProperty(i) && EXCLUDED_PARAMS.indexOf(i) > 0) {
        delete params[i]
      }
    }
  }
  const existParamString = getExistParamString(existParams),
    paramString = getParamString(params)
  if (url[url.length - 1] !== '/') {
    url += '/'
  }
  if (paramString || existParamString) {
    url += '?'
  }
  if (paramString) {
    url += paramString
  }
  if (existParamString) {
    if (url[url.length - 1] !== '?') {
      url += '&'
    }
    url += existParamString
  }
  return url
}

/**
 * Получаем основной домен
 * @param {string} [hostname] Полный домен с поддоменами
 * @returns {string} Основной домен.
 */
export const getDomain = hostname => {
  if (!hostname) {
    hostname = location.hostname
  }
  let parts = hostname.split('.')
  if (parts.length > 2) {
    parts = parts.slice(-2)
  }
  return parts.join('.')
}

/**
 * Получаем pathname
 * @returns {string}  Текущий pathname
 */
export const getPathname = () => {
  return location.pathname
}
